import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import { apiErrorMessage } from "../../utility/apiError";
import type { RootState } from "../../store";
import {
  ActivityState,
  ActivityType,
  CreateActivityPayload,
  KeepShoppingItem,
  AddToWishlistPayload,
  UpdateWishlistItemPayload,
  WishlistQueryParams,
  WishlistResponse,
  WishlistCheckResponse,
} from "./types";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ECOMMERCE_PERFIX || "/api", // Default or env-based
]
  .filter(Boolean)
  .join("");

// Axios instance with cookies support (activity_session cookie for guest users)
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies (for authenticated users)
api.interceptors.request.use(
  async (config) => {
    const token =
      getCookie("access_token") || localStorage.getItem("access_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ==========================================
// Activity Tracking Thunks
// ==========================================

export const recordActivity = createAsyncThunk(
  "activity/recordActivity",
  async (data: CreateActivityPayload, { rejectWithValue }) => {
    try {
      const response = await api.post("/activity", data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to record activity"),
      );
    }
  },
);

export const getKeepShopping = createAsyncThunk(
  "activity/getKeepShopping",
  async (limit: number | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get<KeepShoppingItem[]>("/activity/keep-shopping", {
        params: { limit },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch keep shopping data"),
      );
    }
  },
  {
    condition: (limit, { getState }) => {
      const { keepShopping, keepShoppingLoaded, loading } = (getState() as RootState).activity;
      if (loading) return false;
      const requestedLimit = limit ?? 10;
      if (keepShoppingLoaded && keepShopping.length < requestedLimit) {
        return true;
      }
      return !keepShoppingLoaded;
    },
  },
);

export const getRecentSearches = createAsyncThunk(
  "activity/getRecentSearches",
  async (limit: number | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get<string[]>("/activity/recent-searches", {
        params: { limit },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch recent searches"),
      );
    }
  },
);

export const getRecentCategories = createAsyncThunk(
  "activity/getRecentCategories",
  async (limit: number | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get<string[]>("/activity/recent-categories", {
        params: { limit },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch recent categories"),
      );
    }
  },
);

// ==========================================
// Customer Wishlist Thunks
// ==========================================

export const addToWishlist = createAsyncThunk(
  "activity/addToWishlist",
  async (data: AddToWishlistPayload, { rejectWithValue }) => {
    try {
      const response = await api.post("/activity/wishlist", data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to add to wishlist"),
      );
    }
  },
);

export const getWishlist = createAsyncThunk(
  "activity/getWishlist",
  async (params: WishlistQueryParams | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get<WishlistResponse>("/activity/wishlist", {
        params,
      });
      const body: any = response.data;
      return (body?.items ? body : body?.data ?? body) as WishlistResponse;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to fetch wishlist"),
      );
    }
  },
);

export const checkWishlist = createAsyncThunk(
  "activity/checkWishlist",
  async (
    {
      productId,
      productVariantId,
    }: { productId: string; productVariantId?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get<WishlistCheckResponse>(
        `/activity/wishlist/check/${productId}`,
        { params: { productVariantId } },
      );
      return { productId, ...response.data };
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to check wishlist status"),
      );
    }
  },
);

export const updateWishlistItem = createAsyncThunk(
  "activity/updateWishlistItem",
  async (
    { productId, ...payload }: UpdateWishlistItemPayload,
    { rejectWithValue },
  ) => {
    try {
      const response = await api.patch(
        `/activity/wishlist/${productId}`,
        payload,
      );
      return { productId, ...response.data };
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to update wishlist item"),
      );
    }
  },
);

export const removeFromWishlist = createAsyncThunk(
  "activity/removeFromWishlist",
  async (
    {
      productId,
      productVariantId,
    }: { productId: string; productVariantId?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.delete(
        `/activity/wishlist/${productId}`,
        { params: { productVariantId } },
      );
      return { productId, ...response.data };
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to remove from wishlist"),
      );
    }
  },
);

export const clearWishlist = createAsyncThunk(
  "activity/clearWishlist",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.delete("/activity/wishlist");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to clear wishlist"),
      );
    }
  },
);

// ==========================================
// Initial State
// ==========================================

const initialState: ActivityState = {
  // Activity state
  keepShopping: [],
  recentSearches: [],
  recentCategories: [],
  keepShoppingLoaded: false,
  recentSearchesLoaded: false,
  recentCategoriesLoaded: false,

  // Wishlist state
  wishlist: [],
  wishlistTotal: 0,
  wishlistPagination: null,
  wishlistCheckMap: {},
  wishlistLoading: false,
  wishlistError: null,

  // Global activity state
  loading: false,
  error: null,
  success: false,
};

// ==========================================
// Slice Definition
// ==========================================

const activitySlice = createSlice({
  name: "activity",
  initialState,
  reducers: {
    clearActivityState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
    clearWishlistState: (state) => {
      state.wishlistLoading = false;
      state.wishlistError = null;
    },
    setWishlistCheck: (
      state,
      action: PayloadAction<{ productId: string; isWishlisted: boolean }>,
    ) => {
      state.wishlistCheckMap[action.payload.productId] =
        action.payload.isWishlisted;
    },
  },
  extraReducers: (builder) => {
    builder
      // Record Activity
      .addCase(recordActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(recordActivity.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const incoming = action.meta.arg.activities;
        if (incoming?.eventType === ActivityType.SEARCH && incoming.searchQuery) {
          const query = incoming.searchQuery.trim();
          if (query && !state.recentSearches.includes(query)) {
            state.recentSearches = [query, ...state.recentSearches.slice(0, 9)];
          }
        }
      })
      .addCase(recordActivity.rejected, (state) => {
        state.loading = false;
      })

      // Get Keep Shopping
      .addCase(getKeepShopping.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        getKeepShopping.fulfilled,
        (state, action: PayloadAction<KeepShoppingItem[]>) => {
          state.loading = false;
          state.keepShopping = action.payload;
          state.keepShoppingLoaded = true;
        },
      )
      .addCase(getKeepShopping.rejected, (state) => {
        state.loading = false;
      })

      // Get Recent Searches
      .addCase(getRecentSearches.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        getRecentSearches.fulfilled,
        (state, action: PayloadAction<string[]>) => {
          state.loading = false;
          state.recentSearches = action.payload;
          state.recentSearchesLoaded = true;
        },
      )
      .addCase(getRecentSearches.rejected, (state) => {
        state.loading = false;
      })

      // Get Recent Categories
      .addCase(getRecentCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        getRecentCategories.fulfilled,
        (state, action: PayloadAction<string[]>) => {
          state.loading = false;
          state.recentCategories = action.payload;
          state.recentCategoriesLoaded = true;
        },
      )
      .addCase(getRecentCategories.rejected, (state) => {
        state.loading = false;
      })

      // Add To Wishlist
      .addCase(addToWishlist.pending, (state, action) => {
        state.wishlistLoading = true;
        state.wishlistError = null;
        if (action.meta.arg.productId) {
          state.wishlistCheckMap[action.meta.arg.productId] = true;
        }
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.wishlistLoading = false;
        if (action.meta.arg.productId) {
          state.wishlistCheckMap[action.meta.arg.productId] = true;
        }
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.wishlistLoading = false;
        state.wishlistError = action.payload as string;
        if (action.meta.arg.productId) {
          state.wishlistCheckMap[action.meta.arg.productId] = false;
        }
      })

      // Get Wishlist
      .addCase(getWishlist.pending, (state) => {
        state.wishlistLoading = true;
        state.wishlistError = null;
      })
      .addCase(
        getWishlist.fulfilled,
        (state, action: PayloadAction<WishlistResponse>) => {
          state.wishlistLoading = false;
          state.wishlist = action.payload.items || [];
          state.wishlistTotal = action.payload.total || action.payload.items?.length || 0;
          state.wishlistPagination = action.payload.pagination || null;

          action.payload.items?.forEach((item) => {
            if (item._id) state.wishlistCheckMap[item._id] = true;
            if (item.ssin) state.wishlistCheckMap[item.ssin] = true;
          });
        },
      )
      .addCase(getWishlist.rejected, (state, action) => {
        state.wishlistLoading = false;
        state.wishlistError = action.payload as string;
      })

      // Check Wishlist
      .addCase(checkWishlist.fulfilled, (state, action) => {
        if (action.payload.productId) {
          state.wishlistCheckMap[action.payload.productId] =
            Boolean(action.payload.isWishlisted);
        }
      })

      // Update Wishlist Item
      .addCase(updateWishlistItem.fulfilled, (state, action) => {
        const item = state.wishlist.find(
          (w) => w._id === action.payload.productId || w.ssin === action.payload.productId,
        );
        if (item) {
          if (action.meta.arg.note !== undefined) item.note = action.meta.arg.note;
          if (action.meta.arg.productVariantId !== undefined) {
            item.productVariantId = action.meta.arg.productVariantId;
          }
        }
      })

      // Remove From Wishlist
      .addCase(removeFromWishlist.pending, (state, action) => {
        state.wishlistLoading = true;
        const targetId = action.meta.arg.productId;
        if (targetId) {
          state.wishlistCheckMap[targetId] = false;
          state.wishlist = state.wishlist.filter(
            (w) => w._id !== targetId && w.ssin !== targetId,
          );
          state.wishlistTotal = Math.max(0, state.wishlistTotal - 1);
        }
      })
      .addCase(removeFromWishlist.fulfilled, (state) => {
        state.wishlistLoading = false;
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.wishlistLoading = false;
        state.wishlistError = action.payload as string;
        if (action.meta.arg.productId) {
          state.wishlistCheckMap[action.meta.arg.productId] = true;
        }
      })

      // Clear Wishlist
      .addCase(clearWishlist.fulfilled, (state) => {
        state.wishlist = [];
        state.wishlistTotal = 0;
        state.wishlistPagination = null;
        state.wishlistCheckMap = {};
        state.wishlistLoading = false;
      });
  },
});

export const { clearActivityState, clearWishlistState, setWishlistCheck } =
  activitySlice.actions;

export default activitySlice.reducer;
